import type { PortalDto, PortalStatusDto } from '@bqcrm/shared';
import { Button, Card, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconCalendarShare, IconEye, IconLinkOff, IconSend } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../api';
import { QueryState } from '../../components/QueryState';
import { ask } from '../../confirm';
import { formatDateTime } from '../../format';
import { notifyError, notifyOk } from '../../notify';
import { PortalView } from '../portal/PortalView';

function SendDialog({ partyId, status, onClose }: { partyId: string; status: PortalStatusDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState(status.active?.recipientEmail ?? status.suggestedEmail ?? '');
  const [companyId, setCompanyId] = useState<string | null>(status.suggestedCompanyId);
  const send = useMutation({
    mutationFn: () => api<PortalStatusDto>(`/parties/${partyId}/client-portal`, { method: 'POST', body: { email, companyId } }),
    onSuccess: (s) => {
      qc.setQueryData(['client-portal', partyId], s);
      void qc.invalidateQueries({ queryKey: ['case-timeline'] });
      notifyOk('Wysłano link do strefy klienta');
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title={status.active ? 'Ponowne wysłanie linku do strefy klienta' : 'Wysłanie linku do strefy klienta'}>
      <Stack>
        <Text size="sm">
          {status.partyName} dostanie e-mail z linkiem do strefy klienta ze wszystkimi swoimi sprawami. Przy pierwszym wejściu potwierdza adres jednorazowym kodem wysłanym na ten sam adres.
        </Text>
        {status.active && (
          <Text size="sm" c="orange.8">
            Dotychczasowy link przestanie działać.
          </Text>
        )}
        <TextInput label="Adres e-mail klienta" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
        {status.companies.length > 1 && (
          <Select label="Od firmy" value={companyId} onChange={setCompanyId} allowDeselect={false} data={status.companies.map((x) => ({ value: x.id, label: x.name }))} />
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button onClick={() => send.mutate()} loading={send.isPending} disabled={!email.trim()}>
            Wyślij link
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function PreviewDialog({ partyId, onClose }: { partyId: string; onClose: () => void }) {
  const mobile = useMediaQuery('(max-width: 48em)');
  const q = useQuery({ queryKey: ['client-portal-preview', partyId], queryFn: () => api<PortalDto>(`/parties/${partyId}/client-portal/preview`) });
  return (
    <Modal opened onClose={onClose} title="Podgląd strefy klienta" size="80rem" fullScreen={mobile}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        <Text size="sm" c="dimmed" mb="md">
          Tak widzi to klient (pobieranie plików jest tu wyłączone).
        </Text>
        {q.data && <PortalView portal={q.data} files={null} />}
      </QueryState>
    </Modal>
  );
}

/**
 * Strefa klienta (docs/18): strefa kartoteki klienta — wszystkie jego sprawy (harmonogram prac, umowy, oferty, protokoły,
 * faktury). Karta klienta (`inCase` brak) i karta w sprawie (strefa klienta sprawy).
 */
export function ClientPortalCard({ partyId, inCase }: { partyId: string; inCase?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['client-portal', partyId], queryFn: () => api<PortalStatusDto>(`/parties/${partyId}/client-portal`) });
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);
  const revoke = useMutation({
    mutationFn: () => api<PortalStatusDto>(`/parties/${partyId}/client-portal`, { method: 'DELETE' }),
    onSuccess: (s) => {
      qc.setQueryData(['client-portal', partyId], s);
      notifyOk('Wyłączono link do strefy klienta');
    },
    onError: notifyError,
  });
  const s = q.data;
  if (q.isLoading || q.error || !s) return null;
  if (!s.active && !s.canSend) return null;
  return (
    <Card withBorder padding="sm" mb="sm">
      <Group justify="space-between" wrap="wrap" gap="xs" align="flex-start">
        <Group gap="xs" wrap="nowrap" align="flex-start" style={{ flex: '1 1 300px' }}>
          <IconCalendarShare size={20} style={{ flexShrink: 0, marginTop: 2, opacity: 0.8 }} />
          <div>
            <Text fw={700} size="sm">
              {inCase ? 'Strefa klienta — wszystkie sprawy klienta, w tym ta' : 'Strefa klienta — wszystkie sprawy klienta'}
            </Text>
            {s.active ? (
              <Text size="sm" c="dimmed">
                Link wysłany do {s.active.recipientEmail} · {formatDateTime(s.active.createdAt)}
                {s.active.createdByName ? ` (${s.active.createdByName})` : ' (automatycznie po podpisie umowy)'} ·{' '}
                {s.active.lastViewedAt ? `ostatnio otwarta ${formatDateTime(s.active.lastViewedAt)}` : 'klient jeszcze nie otworzył'}
              </Text>
            ) : (
              <Text size="sm" c="dimmed">
                Klient nie ma aktywnego linku{inCase ? ' — wyjdzie sam po podpisie umowy albo wyślij go teraz' : ''}.
              </Text>
            )}
            <Text size="xs" c="dimmed">
              Harmonogram prac, umowy, oferty, protokoły i faktury ze wszystkich spraw klienta. Przy pracy klient widzi tytuł, branżę i dni — bez osób, godzin i opisu dla ekipy.
            </Text>
          </div>
        </Group>
        <Group gap="xs">
          <Button size="xs" variant="default" leftSection={<IconEye size={14} />} onClick={() => setPreview(true)}>
            Zobacz jak klient
          </Button>
          {s.canSend && (
            <Button size="xs" variant={s.active ? 'default' : 'filled'} leftSection={<IconSend size={14} />} onClick={() => setSending(true)}>
              {s.active ? 'Wyślij ponownie' : 'Wyślij link'}
            </Button>
          )}
          {s.active && s.canSend && (
            <Button
              size="xs"
              variant="subtle"
              color="red"
              leftSection={<IconLinkOff size={14} />}
              loading={revoke.isPending}
              onClick={async () => {
                if (await ask('Klient straci dostęp do harmonogramu i dokumentów wszystkich swoich spraw pod obecnym linkiem.', { title: 'Wyłączyć link do strefy klienta?', confirm: 'Wyłącz', danger: true })) revoke.mutate();
              }}
            >
              Wyłącz link
            </Button>
          )}
        </Group>
      </Group>
      {sending && <SendDialog partyId={partyId} status={s} onClose={() => setSending(false)} />}
      {preview && <PreviewDialog partyId={partyId} onClose={() => setPreview(false)} />}
    </Card>
  );
}
